import { type JSX, useState, type MouseEvent, type ReactElement, type ReactNode, type RefObject } from 'react'
import type { TFunction } from 'shared/translations'
import Button from '@precis-digital/kurama/Button'
import { DatePickerV2Trigger, type DatePickerV2TriggerProps } from '@precis-digital/kurama/DatePickerV2'
import Dropdown from '@precis-digital/kurama/Dropdown'
import Grid from '@precis-digital/kurama/Grid'
import MoreIcon from '@precis-digital/kurama/icons/MoreIcon'
import { type AutocompleteOptionItem } from '@precis-digital/kurama/Autocomplete'
import { type FieldValues, type UseFormSetValue } from 'react-hook-form'
import { type OperatorWithInputType } from 'shared/constants/filterOperators'
import type { FilterTranslationNamespace } from './types'
import OverScreen, { useOverScreen } from 'shared/overScreens/niceModalReact'
import { type FilterFlyoutProps, type Item } from './FilterFlyout'
import AddFiltersGrid from './AddFiltersGrid'

export type { FilterTFunction, FilterTranslationNamespace } from './types'

export interface Option<ValueType = string> {
  value: ValueType
  label: string
  operators?: Record<string, OperatorWithInputType>
  options?: Option[]
  subCategory?: string
  unit?: string | null
}

export interface FilterFormValues {
  parameter: Option
  operator: OperatorWithInputType | null
  data: string | Option | Option[] | number | null
}

export interface ActiveFilter<T extends string = string> {
  parameter: Option<T>
  operator: string
  data: string | Option | AutocompleteOptionItem[] | null
}

interface FilterProps<TFormValues extends FieldValues, TFilter> {
  renderMoreMenu?: (closeMenu: () => void) => ReactElement
  datePickerV2TriggerProps: DatePickerV2TriggerProps | null
  onUpdateFilters: (filters: TFilter[]) => void
  activeFilters: TFilter[]
  parseFilterToChipLabel: (filter: TFilter) => string
  renderListItemLabel: (filter: TFilter) => ReactNode
  additionalCustomControls?: ReactNode
  t: TFunction<FilterTranslationNamespace>
  filterItems: Array<Item<TFormValues>>
  updateFormValues: (filter: TFilter, setValue: UseFormSetValue<TFormValues>) => void
  transformFormValuesToFilters: (formValues: TFormValues) => TFilter
  filterBarRef?: RefObject<HTMLDivElement | null>
  datePickerRef?: RefObject<HTMLDivElement | null>
  isFilterDisabled?: (filter: TFilter) => boolean
  disabledFilterTooltip?: string
}

const Filter = <TFormValues extends FieldValues, TFilter>({
  updateFormValues,
  transformFormValuesToFilters,
  datePickerV2TriggerProps,
  renderMoreMenu,
  activeFilters,
  onUpdateFilters,
  parseFilterToChipLabel,
  renderListItemLabel,
  additionalCustomControls,
  t,
  filterItems,
  filterBarRef,
  datePickerRef,
  isFilterDisabled,
  disabledFilterTooltip
}: FilterProps<TFormValues, TFilter>): JSX.Element => {
  const filterFlyout = useOverScreen('globalFilterFlyout')

  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<HTMLButtonElement | null>(null)
  const handleMoreButtonClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation()
    setMoreMenuAnchorEl(e.currentTarget)
  }

  const handleMoreMenuClose = (): void => {
    setMoreMenuAnchorEl(null)
  }
  const isMoreMenuOpen = moreMenuAnchorEl != null

  const handleOnFilterClick = ({ showActiveFilters }: { showActiveFilters?: boolean } = {}): void => {
    void OverScreen.show<FilterFlyoutProps<TFormValues, TFilter>>('globalFilterFlyout', {
      updateFormValues,
      transformFormValuesToFilters,
      items: filterItems,
      activeFilters,
      onUpdateFilters,
      parseFilterToListItemLabel: renderListItemLabel,
      showActiveFilters,
      t,
      onHide: () => {
        filterFlyout.remove()
      },
      isFlyoutOpen: true,
      isFilterDisabled,
      disabledFilterTooltip
    })
  }

  return (
    <>
      <Grid display="flex" item xs justifyContent="space-between" gap="32px" alignItems="center" minWidth={0}>
        <AddFiltersGrid
          activeFilters={activeFilters}
          handleOnFilterClick={handleOnFilterClick}
          parseFilterToChipLabel={parseFilterToChipLabel}
          onUpdateFilters={onUpdateFilters}
          t={t}
          containerRef={filterBarRef}
          isFilterDisabled={isFilterDisabled}
          disabledFilterTooltip={disabledFilterTooltip}
        />

        <Grid item display="flex" gap="8px" flexShrink={0}>
          {datePickerV2TriggerProps != null ? (
            <Grid ref={datePickerRef}>
              <DatePickerV2Trigger {...datePickerV2TriggerProps} />
            </Grid>
          ) : null}
          {additionalCustomControls}
          {renderMoreMenu != null && (
            <Grid container alignItems="center" height="100%" justifyContent="center">
              <Button
                fullWidth
                variant="outlined"
                onClick={handleMoreButtonClick}
                aria-label="more-menu"
                rightIcon={<MoreIcon />}
              >
                {t('buttons.more')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
      {renderMoreMenu != null && (
        <Dropdown open={isMoreMenuOpen} anchorEl={moreMenuAnchorEl} onClose={handleMoreMenuClose}>
          {renderMoreMenu(handleMoreMenuClose)}
        </Dropdown>
      )}
    </>
  )
}

export default Filter
